import { getPrintDocumentError } from '@Constants/getPrintDocumentError';
import { useFileDownload } from '@Hooks/useFileDownload';
import { useOpenPrintMenu } from '@Hooks/useOpenPrintMenu';
import { useDocumentsRepository } from '@Hooks/useRepository';
import { PrintOptionsRepository } from '@Repositories/Documents';
import { ResponseResolver, useLogger, useToasts } from '@synqo/frontend-component-library';
import { useMutation } from '@tanstack/react-query';

interface PrintDocumentPayload {
    printOption: string;
    primaryId: string;
    mode?: 'download' | 'print';
}

type DownloadResponse = [Blob | string, Response];
const responseResolver: ResponseResolver<DownloadResponse> = async (res) => {
    if (!res.ok) {
        return [await res.text(), res];
    }

    return [await res.blob(), res];
};

export function usePrintDocument() {
    const printOptions = useDocumentsRepository(PrintOptionsRepository);
    const { warn } = useLogger();
    const { pushToast } = useToasts();
    const { triggerDownload } = useFileDownload();
    const openPrintMenu = useOpenPrintMenu();

    const { mutateAsync: downloadPdf } = useMutation({
        mutationFn: async ({ printOption, primaryId, mode }: PrintDocumentPayload): Promise<void> => {
            const [blobOrMessage, response] = await printOptions.printDocument(
                printOption,
                primaryId,
                false,
                responseResolver,
            );

            if (!response.ok) {
                pushToast({ message: getPrintDocumentError(printOption), variant: 'error' });
                warn(
                    `Printing ${printOption} for ${primaryId} returned ${response.status}: ${blobOrMessage as string}`,
                );
                return;
            }

            const blob = blobOrMessage as Blob;

            switch (mode) {
                case 'download': {
                    triggerDownload(blob, response);
                    break;
                }
                case 'print': {
                    openPrintMenu(blob);
                    break;
                }
            }
        },
    });

    return downloadPdf;
}
